import {
  ConflictException,
  ForbiddenException,
  Injectable,
  NotFoundException,
} from '@nestjs/common';
import { PrismaService } from '@cart/api-data-access';
import { CreateThemeRequest, UpdateThemeRequest } from '@cart/shared-types';

const THEME_SELECT = {
  id: true,
  name: true,
  slug: true,
  description: true,
  previewImageUrl: true,
  settings: true,
  isPublished: true,
  createdAt: true,
  updatedAt: true,
};

const PLATFORM_ROLES = ['admin', 'super_admin', 'platform_admin'];

@Injectable()
export class ThemesService {
  constructor(private readonly prisma: PrismaService) {}

  // ── Platform: list all themes (published + draft) ─────────────────────────

  async listAll() {
    const items = await this.prisma.theme.findMany({
      where: { isActive: true, deletedAt: null },
      select: THEME_SELECT,
      orderBy: [{ isPublished: 'desc' }, { sortOrder: 'asc' }, { createdAt: 'desc' }],
    });
    return { items, total: items.length };
  }

  // ── Merchant: list only published themes ──────────────────────────────────

  async listPublished() {
    const items = await this.prisma.theme.findMany({
      where: { isPublished: true, isActive: true, deletedAt: null },
      select: THEME_SELECT,
      orderBy: [{ sortOrder: 'asc' }, { name: 'asc' }],
    });
    return { items, total: items.length };
  }

  // ── Storefront: resolve active theme for a store ──────────────────────────

  async resolveStoreTheme(storeId: string) {
    const store = await this.prisma.store.findUnique({
      where: { id: storeId },
      select: {
        theme: { select: THEME_SELECT },
      },
    });
    if (!store) throw new NotFoundException('Store not found');
    return store.theme ?? null;
  }

  // ── Platform: get single theme ────────────────────────────────────────────

  async getById(id: string) {
    const theme = await this.prisma.theme.findFirst({
      where: { id, isActive: true, deletedAt: null },
      select: THEME_SELECT,
    });
    if (!theme) throw new NotFoundException(`Theme ${id} not found`);
    return theme;
  }

  // ── Platform: create ──────────────────────────────────────────────────────

  async create(dto: CreateThemeRequest, actorId: string) {
    const slug = this.slugify(dto.name);
    const existing = await this.prisma.theme.findUnique({ where: { slug } });
    if (existing) throw new ConflictException(`Theme slug "${slug}" already exists`);

    return this.prisma.theme.create({
      data: {
        name: dto.name,
        slug,
        description: dto.description ?? null,
        previewImageUrl: dto.previewImageUrl ?? null,
        settings: dto.settings as object,
        isPublished: false,
        isActive: true,
        createdBy: actorId,
        updatedBy: actorId,
      },
      select: THEME_SELECT,
    });
  }

  // ── Platform: update ──────────────────────────────────────────────────────

  async update(id: string, dto: UpdateThemeRequest, actorId: string) {
    const theme = await this.getById(id);

    const updates: Record<string, unknown> = { updatedBy: actorId };
    if (dto.name !== undefined) {
      updates['name'] = dto.name;
      updates['slug'] = this.slugify(dto.name);
    }
    if (dto.description !== undefined) updates['description'] = dto.description;
    if (dto.previewImageUrl !== undefined) updates['previewImageUrl'] = dto.previewImageUrl;
    if (dto.settings !== undefined) {
      updates['settings'] = { ...(theme.settings as object), ...(dto.settings as object) };
    }

    return this.prisma.theme.update({
      where: { id },
      data: updates,
      select: THEME_SELECT,
    });
  }

  // ── Platform: publish / unpublish ────────────────────────────────────────

  async setPublished(id: string, published: boolean, actorId: string) {
    await this.getById(id);
    await this.prisma.theme.update({
      where: { id },
      data: { isPublished: published, updatedBy: actorId },
    });
    return { success: true, isPublished: published };
  }

  // ── Platform: soft delete ─────────────────────────────────────────────────

  async remove(id: string, actorId: string) {
    await this.getById(id);

    // Detach from any stores using this theme before deleting
    await this.prisma.store.updateMany({
      where: { themeId: id },
      data: { themeId: null, updatedBy: actorId },
    });

    await this.prisma.theme.update({
      where: { id },
      data: { isActive: false, deletedAt: new Date(), deletedBy: actorId, updatedBy: actorId },
    });
    return { success: true };
  }

  // ── Merchant: apply theme to store ───────────────────────────────────────

  async applyToStore(storeId: string, themeId: string, actorId: string, actorRole: string) {
    const theme = await this.prisma.theme.findFirst({
      where: { id: themeId, isPublished: true, isActive: true, deletedAt: null },
      select: { id: true },
    });
    if (!theme) throw new NotFoundException('Theme not found or not published');

    // Platform roles can apply to any store; merchants must own the store
    if (!PLATFORM_ROLES.includes(actorRole)) {
      const staff = await this.prisma.storeStaff.findFirst({
        where: { storeId, userId: actorId, isActive: true, deletedAt: null },
        select: { id: true },
      });
      if (!staff) throw new ForbiddenException('You do not have access to this store');
    }

    await this.prisma.store.update({
      where: { id: storeId },
      data: { themeId, updatedBy: actorId },
    });
    return { success: true, themeId };
  }

  // ── Merchant: get their store's current theme ─────────────────────────────

  async getStoreTheme(storeId: string) {
    return this.resolveStoreTheme(storeId);
  }

  private slugify(name: string): string {
    return name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }
}
